import { memo, type ReactNode } from 'react';

const inlineTokenRe = /(!\[[^\]]*]\([^)]+\)|\[[^\]]+]\([^)]+\)|`[^`]+`|\*\*[^*]+\*\*|(?:https?:\/\/|\/api\/alty\/generated\/)\S+?\.(?:png|jpe?g|gif|webp)(?:\?\S*)?)/gi;
const imageExtRe = /\.(png|jpe?g|gif|webp)(?:\?.*)?$/i;

function safeUrl(url: string): string | null {
  const trimmed = url.trim();
  if (trimmed.startsWith('/api/alty/generated/')) return trimmed;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return null;
}

function markdownImage(part: string): { alt: string; url: string } | null {
  const match = /^!\[([^\]]*)]\(([^)\s]+)(?:\s+"[^"]*")?\)$/.exec(part);
  if (!match) return null;
  return { alt: match[1] || 'Generated image', url: match[2] };
}

function markdownLink(part: string): { label: string; url: string } | null {
  const match = /^\[([^\]]+)]\(([^)\s]+)(?:\s+"[^"]*")?\)$/.exec(part);
  if (!match) return null;
  return { label: match[1], url: match[2] };
}

function renderImage(url: string, alt: string, key: number): ReactNode {
  const safe = safeUrl(url);
  if (!safe || !imageExtRe.test(safe)) return `![${alt}](${url})`;
  return (
    <a
      key={key}
      className="alty-markdown-image-link"
      href={safe}
      target="_blank"
      rel="noopener noreferrer"
    >
      <img className="alty-markdown-image" src={safe} alt={alt || 'Generated image'} loading="lazy" />
    </a>
  );
}

function inline(text: string): ReactNode[] {
  const parts = text.split(inlineTokenRe).filter(Boolean);
  return parts.map((part, index) => {
    const image = markdownImage(part);
    if (image) return renderImage(image.url, image.alt, index);
    const link = markdownLink(part);
    if (link) {
      const safe = safeUrl(link.url);
      if (safe) {
        return (
          <a key={index} href={safe} target="_blank" rel="noopener noreferrer">
            {link.label}
          </a>
        );
      }
    }
    if (imageExtRe.test(part) && safeUrl(part)) return renderImage(part, 'Generated image', index);
    if (part.startsWith('`') && part.endsWith('`')) {
      return <code key={index}>{part.slice(1, -1)}</code>;
    }
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
}

function lineIndentLen(line: string): number {
  const match = /^(\s*)/.exec(line);
  return match ? match[1].length : 0;
}

const orderedItemRe = /^(\s*)(\d+)\.\s+(.*)$/;
const bulletItemRe = /^(\s*)[-*]\s+(.*)$/;
const tableSeparatorCellRe = /^\s*:?-{2,}:?\s*$/;

// Parse a single pipe-delimited row into cell strings. Accepts both the
// GitHub-flavoured `| a | b |` form (leading + trailing pipes) and the
// bare `a | b` form. Returns `null` if the line clearly isn't a row.
function parseTableRow(line: string): string[] | null {
  const trimmed = line.trim();
  if (!trimmed.includes('|')) return null;
  const stripped = trimmed.replace(/^\|/, '').replace(/\|$/, '');
  return stripped.split('|').map((cell) => cell.trim());
}

function isTableSeparator(line: string): boolean {
  const cells = parseTableRow(line);
  if (!cells || cells.length === 0) return false;
  return cells.every((cell) => tableSeparatorCellRe.test(cell));
}

// Cell alignment from a separator row: `:---` → left, `:---:` → center,
// `---:` → right, plain `---` → no explicit alignment.
function tableCellAlignment(cell: string): 'left' | 'center' | 'right' | undefined {
  const trimmed = cell.trim();
  const startsColon = trimmed.startsWith(':');
  const endsColon = trimmed.endsWith(':');
  if (startsColon && endsColon) return 'center';
  if (endsColon) return 'right';
  if (startsColon) return 'left';
  return undefined;
}

// Collect a contiguous list (ordered or bullet) starting at `startIdx`. Handles
// blank lines between items and indented continuation content (sub-lists or
// trailing paragraphs) so a numbered list keeps numbering across the
// interruption instead of restarting from 1 in a new <ol>.
function collectList(
  lines: string[],
  startIdx: number,
  indent: number,
  kind: 'ordered' | 'bullet',
): { items: string[][]; nextIdx: number } {
  const items: string[][] = [];
  let i = startIdx;
  // Marker width: "1. " == 3 / "- " == 2. Continuation is any line indented at
  // least to the content column.
  const markerWidth = kind === 'ordered' ? 3 : 2;
  const continuationIndent = indent + markerWidth;
  const itemRe = kind === 'ordered' ? orderedItemRe : bulletItemRe;

  const startsItem = (line: string): RegExpExecArray | null => {
    const match = itemRe.exec(line);
    if (!match || match[1].length !== indent) return null;
    return match;
  };

  while (i < lines.length) {
    const cur = lines[i];

    if (!cur.trim()) {
      let j = i + 1;
      while (j < lines.length && !lines[j].trim()) j += 1;
      if (j >= lines.length) { i = j; break; }
      const next = lines[j];
      if (startsItem(next) || lineIndentLen(next) >= continuationIndent) {
        if (items.length > 0) items[items.length - 1].push('');
        i = j;
        continue;
      }
      break;
    }

    const match = startsItem(cur);
    if (match) {
      items.push([match[match.length - 1]]);
      i += 1;
      continue;
    }

    if (lineIndentLen(cur) >= continuationIndent && items.length > 0) {
      items[items.length - 1].push(cur.slice(continuationIndent));
      i += 1;
      continue;
    }

    break;
  }

  return { items, nextIdx: i };
}

function renderItemContent(lines: string[]): ReactNode {
  const trimmed = lines.slice();
  while (trimmed.length && !trimmed[0].trim()) trimmed.shift();
  while (trimmed.length && !trimmed[trimmed.length - 1].trim()) trimmed.pop();
  if (trimmed.length === 0) return null;

  // Tight item — single paragraph of inline content — render without a <p>
  // wrapper so simple list items stay compact.
  const hasComplexBlock = trimmed.some((line, idx) =>
    (idx > 0 && /^\s*[-*]\s+/.test(line)) ||
    (idx > 0 && /^\s*\d+\.\s+/.test(line)) ||
    line.trim().startsWith('```') ||
    !line.trim(),
  );

  if (!hasComplexBlock) return inline(trimmed.join('\n'));
  return <>{parseBlocks(trimmed)}</>;
}

function parseBlocks(lines: string[]): ReactNode[] {
  const blocks: ReactNode[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) {
      i += 1;
      continue;
    }

    if (line.trim().startsWith('```')) {
      const language = line.trim().slice(3).trim();
      const code: string[] = [];
      i += 1;
      while (i < lines.length && !lines[i].trim().startsWith('```')) {
        code.push(lines[i]);
        i += 1;
      }
      if (i < lines.length) i += 1;
      blocks.push(
        <pre key={blocks.length}>
          <code data-language={language || undefined}>{code.join('\n')}</code>
        </pre>,
      );
      continue;
    }

    // GitHub-flavoured table: a `|`-delimited header row followed by a
    // separator row of dashes/colons. Render as an actual <table> wrapped in
    // a horizontally scrollable container so wide tables can be panned
    // inside the chat instead of collapsing into a run-on paragraph.
    if (i + 1 < lines.length && isTableSeparator(lines[i + 1])) {
      const headerCells = parseTableRow(line);
      const separatorCells = parseTableRow(lines[i + 1]);
      if (headerCells && headerCells.length >= 1 && separatorCells) {
        const alignments = separatorCells.map(tableCellAlignment);
        const colCount = headerCells.length;
        const bodyRows: string[][] = [];
        let j = i + 2;
        while (j < lines.length) {
          const rowCells = parseTableRow(lines[j]);
          if (!rowCells) break;
          while (rowCells.length < colCount) rowCells.push('');
          bodyRows.push(rowCells);
          j += 1;
        }
        i = j;
        blocks.push(
          <div key={blocks.length} className="alty-markdown-table-wrap">
            <table className="alty-markdown-table">
              <thead>
                <tr>
                  {headerCells.map((cell, idx) => (
                    <th key={idx} style={alignments[idx] ? { textAlign: alignments[idx] } : undefined}>
                      {inline(cell)}
                    </th>
                  ))}
                </tr>
              </thead>
              {bodyRows.length ? (
                <tbody>
                  {bodyRows.map((row, ridx) => (
                    <tr key={ridx}>
                      {row.slice(0, colCount).map((cell, cidx) => (
                        <td key={cidx} style={alignments[cidx] ? { textAlign: alignments[cidx] } : undefined}>
                          {inline(cell)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              ) : null}
            </table>
          </div>,
        );
        continue;
      }
    }

    const orderedHead = orderedItemRe.exec(line);
    if (orderedHead) {
      const { items, nextIdx } = collectList(lines, i, orderedHead[1].length, 'ordered');
      const startNum = parseInt(orderedHead[2], 10);
      i = nextIdx;
      blocks.push(
        <ol key={blocks.length} start={startNum !== 1 ? startNum : undefined}>
          {items.map((itemLines, idx) => (
            <li key={idx}>{renderItemContent(itemLines)}</li>
          ))}
        </ol>,
      );
      continue;
    }

    const bulletHead = bulletItemRe.exec(line);
    if (bulletHead) {
      const { items, nextIdx } = collectList(lines, i, bulletHead[1].length, 'bullet');
      i = nextIdx;
      blocks.push(
        <ul key={blocks.length}>
          {items.map((itemLines, idx) => (
            <li key={idx}>{renderItemContent(itemLines)}</li>
          ))}
        </ul>,
      );
      continue;
    }

    const paragraph: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() &&
      !lines[i].trim().startsWith('```') &&
      !/^\s*[-*]\s+/.test(lines[i]) &&
      !/^\s*\d+\.\s+/.test(lines[i]) &&
      !(i + 1 < lines.length && lines[i].includes('|') && isTableSeparator(lines[i + 1]))
    ) {
      paragraph.push(lines[i]);
      i += 1;
    }
    if (paragraph.length) {
      blocks.push(<p key={blocks.length}>{inline(paragraph.join('\n'))}</p>);
    }
  }

  return blocks;
}

// Pure leaf — parses whatever it's handed. Memoised by content so a streaming
// "frozen prefix" (see MarkdownTextImpl) doesn't re-parse when only the tail
// changes.
function MarkdownLeafImpl({ text }: { text: string }) {
  return <>{parseBlocks(text.split(/\r?\n/))}</>;
}
const MarkdownLeaf = memo(MarkdownLeafImpl);

// Find the index AFTER the last paragraph break ("\n\n") that we can safely
// treat as a structural boundary: outside an unclosed ```fence``` AND not
// followed by a line that would extend the previous block (an ordered/bullet
// list item, where splitting would visually fragment the list).
//
// Everything strictly before that index is structurally frozen — no future
// append can change how it parses — so we can memoise its parsed output and
// re-parse only the small tail on each streaming tick. Returns -1 when no
// safe split exists (in which case we just parse the whole thing once).
function findStableSplit(text: string): number {
  let inFence = false;
  let lineStart = 0;
  let lastSafe = -1;
  for (let i = 0; i <= text.length; i++) {
    const atEnd = i === text.length;
    if (!atEnd && text.charCodeAt(i) !== 10 /* \n */) continue;
    const line = text.slice(lineStart, i);
    if (line.trim().startsWith('```')) {
      inFence = !inFence;
    } else if (!inFence && line.trim() === '' && !atEnd && i + 1 < text.length) {
      // Peek the next non-blank line. Don't split if it could continue a list
      // (the second leaf would render a fresh <ol>/<ul> instead of extending
      // the prefix's list).
      let p = i + 1;
      while (p < text.length) {
        let nl = p;
        while (nl < text.length && text.charCodeAt(nl) !== 10) nl += 1;
        const peek = text.slice(p, nl);
        if (peek.trim()) {
          if (!/^\s*([-*]|\d+\.)\s+/.test(peek)) lastSafe = i + 1;
          break;
        }
        p = nl + 1;
      }
    }
    if (atEnd) break;
    lineStart = i + 1;
  }
  return lastSafe;
}

// Streaming rebuilds the assistant message on every rAF tick; memoizing keeps
// already-rendered messages (and the streaming one across no-op renders) from
// re-parsing markdown on every list update. For long streaming replies the
// per-tick re-parse of the FULL text was quadratic in response length, so we
// also split a "frozen prefix" out into its own memoised <MarkdownLeaf> — the
// prefix stays referentially stable while the user is mid-response, and only
// the small tail re-parses each tick.
function MarkdownTextImpl({ text }: { text: string }) {
  const split = findStableSplit(text);
  if (split <= 0 || split >= text.length) return <MarkdownLeaf text={text} />;
  return (
    <>
      <MarkdownLeaf text={text.slice(0, split)} />
      <MarkdownLeaf text={text.slice(split)} />
    </>
  );
}
export const MarkdownText = memo(MarkdownTextImpl);
